import { Box, Container, Typography, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PronunciationHint from "../../common-components/PronunciationHint";
import {
  frontendRoutes,
  LANDING_STAY_QUERY_PARAM,
} from "../../utility/frontendRoutes";

// A logged-in visitor hitting "/" is normally bounced straight to the feed
// (see LandingPage.jsx) — this link needs the same "?stay=true" bypass
// LandingFooter.jsx uses for its own logo link, or a logged-in user clicking
// "About Ngwaahia" from here would just be redirected past the page they
// asked to see.
const LANDING_LINK = `${frontendRoutes.LANDING}?${LANDING_STAY_QUERY_PARAM}=true`;

const sectionTitle = {
  fontWeight: 700,
  color: "#1f2937",
  mt: 5,
  mb: 1.5,
};

const body = {
  color: "#374151",
  lineHeight: 1.8,
  mb: 2,
};

const PAGE_TITLE = "What Does Ngwaahia Mean? | Ngwaahia";
const PAGE_DESCRIPTION =
  "Ngwaahia comes from the Igbo ngwa ahịa, meaning goods or merchandise. Learn the story behind the name and what Ngwaahia the marketplace does.";

const WhatIsNgwaahia = () => (
  <Container maxWidth="md" sx={{ py: 6 }}>
    <Helmet>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <link
        rel="canonical"
        href="https://ngwaahia.com/what-does-ngwaahia-mean"
      />
      <meta property="og:title" content={PAGE_TITLE} />
      <meta property="og:description" content={PAGE_DESCRIPTION} />
      <meta
        property="og:url"
        content="https://ngwaahia.com/what-does-ngwaahia-mean"
      />
    </Helmet>

    <Typography variant="h4" fontWeight={800} color="#1f2937" gutterBottom>
      What does "Ngwaahia" mean?
    </Typography>

    <Typography sx={body}>
      Our name, pronounced <PronunciationHint />, comes from the Igbo{" "}
      <em>ngwa ahịa</em> (also written <em>ngwaahịa</em> or <em>ngwa ahia</em>),
      meaning goods, products, or merchandise. We built Ngwaahia to bring the
      spirit of the Nigerian marketplace online.
    </Typography>

    <Typography variant="h6" sx={sectionTitle}>
      What Ngwaahia is
    </Typography>
    <Typography sx={body}>
      Ngwaahia is a marketplace for discovering products from local sellers and
      creating your own online shop. Discover products from sellers across
      Nigeria and beyond, find what you need, connect with sellers, or open your
      own shop and start selling, all from your phone.
    </Typography>

    <Typography variant="h6" sx={sectionTitle}>
      Why the name
    </Typography>
    <Typography sx={body}>
      Nigerian commerce has always run through markets, the physical spaces
      where buyers and sellers meet, haggle, and build trust over time.
      "Ngwaahia" names that idea directly: it's the word for the goods
      themselves, the thing markets exist to trade. Rather than invent an
      abstract brand name, we named the marketplace after the marketplace.
    </Typography>

    <Box sx={{ mt: 6, pt: 3, borderTop: "1px solid #e5e7eb" }}>
      <Typography variant="body2" color="text.secondary">
        Learn more:{" "}
        <MuiLink component={Link} to={LANDING_LINK}>
          About Ngwaahia
        </MuiLink>
      </Typography>
    </Box>
  </Container>
);

export default WhatIsNgwaahia;
